/**
 * @file add-sales-user-modal.tsx
 * @module app/modals/add-sales-user-modal
 * @description Renders the add sales user modal modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Button, Col, Form, FormLabel, InputGroup, Modal, Row } from "react-bootstrap";
import CrossSvg from "../../_admin/assets/media/svg_uTrade/cross-rounded-gray.svg";
import APICallService from "../../api/apiCallService";
import { master, assistAdmin } from "../../api/apiEndPoints";
import { assistAdminJson } from "../../api/apiJSON/sales-rep";
import { success } from "../../Global/toast";
import { REPRESENTATIVE_ROLE } from "../../utils/staticJSON";
import { CustomComponentSelect } from "../custom/Select/CustomComponentSelect";
import { CustomSelect } from "../custom/Select/CustomSelect";
import { CustomSelectWhite } from "../custom/Select/CustomSelectWhite";
import { PAGE_LIMIT } from "../../utils/constants";
import EditSVG from "../../_admin/assets/media/svg_uTrade/edit-round.svg";
import { fileValidation } from "../../Global/fileValidation";
import uploadIcon from "../../_admin/assets/media/svg_uTrade/upload-img-white.svg";
import ImageWithFallback from "../modules/_common/components/image-with-fallback";

interface AddSalesUserModalProps {
    show: boolean;
    onHide: () => void;
    onSave: () => void;
    editData?: any;
}

const AddSalesUserModal: React.FC<AddSalesUserModalProps> = ({
    show,
    onHide,
    onSave,
    editData,
}) => {
    const [form, setForm] = useState<any>({
        name: "",
        email: "",
        phone: "",
        roles: "",
        districts: [],
        salesManagerId: "",
        image: "",
        imageReader: "",
    });
    const [validation, setValidation] = useState<any>({
        name: false,
        email: false,
        phone: false,
        roles: false,
        districts: false,
        salesManagerId: false,
        image: false
    });
    const [loading, setLoading] = useState(false);
    const [fetchLoader, setFetchLoader] = useState(false);
    const [page, setPage] = useState(1);
    const [districts, setDistricts] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [districtDetails, setDistrictDetails] = useState<any>([]);
    const [salesUsers, setSalesUsers] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    // const [showAssignedModal, setShowAssignedModal] = useState(false);
    const [assignedDistricts, setAssignedDistricts] = useState<any[]>([]);
    const [initialDistricts, setInitialDistricts] = useState<any[]>([]);
    const [removedDistricts, setRemovedDistricts] = useState<any[]>([]);

    useEffect(() => {
        if (!show) {
            setForm({ name: "", email: "", phone: "", roles: "", districts: [], salesManagerId: "", image: "", imageReader: "" });
            setValidation({ name: false, email: false, phone: false, roles: false, districts: false, salesManagerId: false, image: false });
            setLoading(false);
            setDistrictDetails([]);
            setAssignedDistricts([]);
            setInitialDistricts([]);
            setRemovedDistricts([]);
        } else if (editData) {
            const roleType = editData?.roleAndPermission?.roleType || (editData?.role && editData?.role[0]?.type);
            setForm({
                name: editData?.name || "",
                email: editData?.email || "",
                phone: editData?.phone || "",
                roles: roleType || "",
                districts: editData?.districts ? editData?.districts.map((d: any) => ({ name: d.name, reference: d.reference || d._id })) : [],
                salesManagerId: editData?.manager?.reference || "",
                image: editData?.image || "",
                imageReader: "",
            });
            if (editData?.districts) {
                const mapped = editData?.districts.map((d: any) => ({
                    value: d.name,
                    name: d.name,
                    id: d.reference || d._id,
                    label: d.name,
                    title: d.name,
                }));
                setDistrictDetails(mapped);
                setInitialDistricts(mapped);
            }

            // When editing an existing sales rep, preload the manager list
            // so that the current manager appears selected in the dropdown.
            if (roleType === 2 || roleType === 7) {
                const managerRole = roleType === 2 ? 1 : 6;
                fetchSalesUsers(1, PAGE_LIMIT, "", managerRole);
            }
        }
    }, [show, editData]);

    useEffect(() => {
        fetchDistrict();
        fetchSalesUsers(1, PAGE_LIMIT, searchTerm, form.roles === 2 ? 1 : form.roles === 7 ? 6 : 1);
    }, []);

    const fetchDistrict = async () => {
        setFetchLoader(true);
        // let params = {
        //   pageNo: page,
        //   limit: limit,
        //   searchTerm: search,
        //   sortKey: 'name',
        //   sortOrder: 1,
        // };
        let apiService = new APICallService(
            master.listDistricts,
            // driverJSON.listDriver(params),
            "",
            "",
            "",
            "",
            ""
        );
        let response = await apiService.callAPI();
        if (response.records) {
            response.records = response.records.map((val: any) => {
                return {
                    value: val.name,
                    name: val.name,
                    id: val._id,
                    label: (
                        <>
                            <label className="form-check-label fs-16 fw-600 text-dark">
                                {val.name}
                            </label>
                        </>
                    ),
                    title: val.name,
                };
            });
            setTotalRecords(response.total);
            let data: any = (prevResults: any) =>
                page === 1 ? response.records : [...prevResults, ...response.records];
            setDistricts(data);
        }
        setFetchLoader(false);
    };

    const handleSelectDistrict = (selectedDistricts: any) => {
        setDistrictDetails(selectedDistricts);

        let districts: any = [];
        selectedDistricts.map((val: any) => {
            districts.push({
                name: val.name,
                reference: val.id,
            });
        });
        let temp = { ...form };
        temp.districts = districts;
        setForm(temp);
        setAssignedDistricts([]); // Clear conflicts when selection changes
        setValidation((prev: any) => ({ ...prev, districts: false }));
        // Detect initially-assigned districts that were removed
        const removed = initialDistricts.filter(
            (init: any) => !selectedDistricts.find((s: any) => s.id === init.id)
        );
        setRemovedDistricts(removed);
    };

    async function checkDistrictAssignment() {
        if (form.districts.length === 0) return;

        setFetchLoader(true);
        const districtIds = form.districts.map((d: any) => d.reference);

        const payload = {
            districts: districtIds
        };

        const apiService = new APICallService(
            assistAdmin.checkAssignedDistrict,
            payload,
            "",
            "",
            "",
            "",
            "",
            ""
        );

        const response = await apiService.callAPI();
        setFetchLoader(false);

        if (response && response.records && response.records.length > 0) {
            const conflicts: any[] = [];
            // Filter out the current user being edited from conflict results
            const otherManagerAssignments = response.records.filter((record: any) => record._id !== editData?._id);

            otherManagerAssignments.forEach((record: any) => {
                record.districts.forEach((dist: any) => {
                    if (districtIds.includes(dist.reference) && !conflicts.find(c => c.reference === dist.reference)) {
                        conflicts.push({
                            reference: dist.reference,
                            name: dist.name
                        });
                    }
                });
            });

            if (conflicts.length > 0) {
                setAssignedDistricts(conflicts);
            } else {
                setAssignedDistricts([]);
            }
        } else {
            setAssignedDistricts([]);
        }
    }



    const onMenuDistrictClose = () => {
        if ((form.roles === 1 || form.roles === 6) && form.districts.length > 0) {
            checkDistrictAssignment();
        }
    };

    const fetchSalesUsers = async (
        page: number,
        limit: number,
        search: string = "",
        role: number = 1
    ) => {
        setFetchLoader(true);
        let params: any = {
            pageNo: 1,
            limit: 0,
            needCount: true,
            searchTerm: search,
            sortKey: "name",
            sortOrder: 1,
            "role[0]": role
        };
        let apiService = new APICallService(
            assistAdmin.list,
            params,
            "",
            "",
            "",
            "",
            "",
            ""
        );
        let response = await apiService.callAPI();
        if (response.records) {
            response.records = response.records.map((val: any) => {
                return {
                    label: (
                      <div className="d-flex flex-column">
                        <span className="fs-16 fw-600 text-dark">{val.name}</span>
                        <span className="fs-12 fw-500 text-gray-600">{val.email}</span>
                      </div>
                    ),
                    value: val.name,
                    _id: val._id,
                    image: val.image,
                };
            });
            setTotalRecords(response.total);
            // let data: any = (prevResults: any) =>
            //     page === 1 ? response.records : [...prevResults, ...response.records];
            setSalesUsers(response.records);
        }
        setFetchLoader(false);
    };
    const onMenuSalesUsersScrollToBottom = async () => {
        if (!(salesUsers.length === totalRecords)) {
            let tempPage = page;
            tempPage = tempPage + 1;
            setPage(tempPage);
            // Fetch drivers using warehouse from global context
            await fetchSalesUsers(
                tempPage,
                PAGE_LIMIT,
                searchTerm,
                form.roles === 2 ? 1 : form.roles === 7 ? 6 : 1
            );
        }
    };
    const onMenuSalesUsersClose = async () => {
        setSearchTerm("");
    };
    const onMenuSalesUsersOpen = async () => {
        setPage(1);
        // Fetch drivers using warehouse from global context
        await fetchSalesUsers(
            1,
            PAGE_LIMIT,
            searchTerm,
            form.roles === 2 ? 1 : form.roles === 7 ? 6 : 1
        );
    };
    const handleSalesUsersInputChange = async (inputValue: string) => {
        setPage(1);
        setSearchTerm(inputValue);
        // Fetch drivers using warehouse from global context
        await fetchSalesUsers(
            1,
            PAGE_LIMIT,
            inputValue,
            form.roles === 2 ? 1 : form.roles === 7 ? 6 : 1
        );
    };

    const handleAssignSalesUser = async (event: any) => {
        let temp = { ...form };
        temp.salesManagerId = event._id;
        setForm(temp);
        setValidation((prev: any) => ({ ...prev, salesManagerId: false }));
    };

    const handleImageInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        let temp = { ...form };
        let tempValidation: any = { ...validation };
        tempValidation.image = true;
        if (!selectedFiles || selectedFiles.length === 0) return;
        else {
            if (fileValidation(selectedFiles[0])) {
                temp.image = URL.createObjectURL(selectedFiles[0]);
                temp.imageReader = selectedFiles[0];
                tempValidation.image = false;
            }
        }
        setValidation(tempValidation);
        setForm(temp);
    };

    const handleChange = (field: any, value: any) => {
        setForm((prev: any) => {
            const updated = { ...prev, [field]: value };

            // When role changes, clear assigned manager and reset related lists
            if (field === "roles") {
                updated.salesManagerId = "";
            }

            return updated;
        });

        if (validation[field]) {
            setValidation((prev: any) => ({ ...prev, [field]: false }));
        }

        if (field === "roles") {
            // Clear any previously loaded reps/managers when role changes
            setSalesUsers([]);
            setAssignedDistricts([]);
        }
    };

    async function handleSave() {
        const tmpValidation: any = {
            name: !form.name.trim(),
            email: !form.email.trim(),
            phone: !form.phone.trim(),
            roles: !form.roles,
            districts: !editData && form.roles == 1 ? form.districts.length === 0 : false,
            salesManagerId: form.roles == 2 ? !form.salesManagerId : form.roles == 7 ? !form.salesManagerId : false,
        };
        setValidation(tmpValidation);

        const isValid = Object.values(tmpValidation).every((v) => !v);
        if (!isValid) return;

        setLoading(true);

        const salesRepData = {
            name: form.name,
            email: form.email,
            phoneCountry: "+255",
            phone: form.phone,
            type: form.roles,
            districts: form.districts.map((d: any) => d.reference),
            manager: form.salesManagerId,
            image: form.imageReader
        };

        // If there are assigned districts, remove them first before saving
        if (assignedDistricts.length > 0) {
            const payload = {
                districts: assignedDistricts.map(d => d.reference)
            };

            let removeSuccess = false;
            while (!removeSuccess) {
                const removeApiService = new APICallService(
                    assistAdmin.removeAssignedDistrict,
                    payload,
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                );

                const removeResponse = await removeApiService.callAPI();
                if (removeResponse) {
                    removeSuccess = true;
                    setAssignedDistricts([]);
                } else {
                    // Polling: wait 2 seconds before retry
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            }
        }


        const apiService = new APICallService(
            editData ? assistAdmin.edit : assistAdmin.add,
            editData ? assistAdminJson.editAssistAdmin(salesRepData) : assistAdminJson.addAssistAdmin(salesRepData),
            {_id : editData ? editData._id : ""},
            "",
            "",
            "",
            "",
            ""
        );

        const response = await apiService.callAPI();

        if (response) {
            success(response.message || `Sales Representative ${editData ? "updated" : "added"} successfully!`);
            onSave();
            onHide();
        } else {
            setLoading(false);
        }
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="modal-lg"
            contentClassName="rounded-15px border-0 shadow-sm"
        >
            <Modal.Header className="border-0 pb-0 position-relative d-flex justify-content-center pt-8">
                <img
                    className="close-inner-top position-absolute start-0 ms-5"
                    width={40}
                    height={40}
                    src={CrossSvg}
                    alt="closebutton"
                    onClick={onHide}
                    style={{ top: "25px", cursor: "pointer" }}
                />
                <Modal.Title className="fw-bolder fs-20 text-dark">{editData ? "Edit User!" : "Add New User!"}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-10 pb-10 pt-4">
                <div className="d-flex justify-content-center mb-7">
                    <label
                        className="image-input image-input-outline rounded-circle bg-white position-relative shadow-sm"
                        style={{ width: "125px", height: "125px", cursor: "pointer" }}
                    >
                        <div className="image-input-wrapper w-100 h-100 rounded-circle overflow-hidden position-relative">
                            {form.image ? (
                                <ImageWithFallback
                                    src={form.image}
                                    alt="Avatar"
                                    className="w-100 h-100 object-fit-cover"
                                    noDefaultSize
                                />
                            ) : (
                                <div className="w-100 h-100 bg-light d-flex align-items-center justify-content-center">
                                    <img
                                        width={168}
                                        height={168}
                                        src={uploadIcon}
                                        className="img-fluid border-r10px"
                                        alt=""
                                    />
                                </div>
                            )}
                        </div>
                        {!loading && (
                            <>
                                <input
                                    type="file"
                                    name="avatar"
                                    accept=".png, .jpg, .jpeg"
                                    onChange={handleImageInput}
                                    className="d-none"
                                />
                                {form.image ? (
                                    <div
                                    className="btn btn-icon btn-circle btn-active-color-primary w-35px h-35px bg-white shadow position-absolute d-flex align-items-center justify-content-center"
                                    style={{ bottom: "0px", right: "0px", zIndex: 1 }}
                                >
                                    <img src={EditSVG} alt="" height={20} width={20} />
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </>
                        )}
                    </label>
                </div>
                <Form className="mt-4">
                    <Row className="mb-7 gap-lg-0 gap-md-4">
                        <Col md={12} lg={6}>
                            <FormLabel className="fs-16 fw-500 mb-3 text-dark">User Name</FormLabel>
                            <Form.Control
                                type="text"
                                className={`form-control-custom bg-white ${validation.name ? "border-danger" : ""}`}
                                placeholder="Type here"
                                value={form.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                            />
                        </Col>
                        <Col md={12} lg={6}>
                            <FormLabel className="fs-16 fw-500 mb-3 text-dark">Email</FormLabel>
                            <Form.Control
                                type="text"
                                className={`form-control-custom bg-white ${validation.email ? "border-danger" : ""}`}
                                placeholder="Type here"
                                value={form.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-7 gap-lg-0 gap-md-4">
                        <Col md={12} lg={6}>
                            <FormLabel className="fs-16 fw-500 mb-3 text-dark">Phone</FormLabel>
                            <InputGroup>
                                <InputGroup.Text
                                    className={`bg-white border-right-0 fs-16 fw-600 text-black px-6 ${validation.phone ? "border-danger" : ""}`}
                                >
                                    +255
                                </InputGroup.Text>
                                <Form.Control
                                    className={`form-control-custom border-active-none bg-white border-left-0 ps-0 form-control-phone-input ${validation.phone ? "border-danger" : ""}`}
                                    name="phone"
                                    placeholder="Type here"
                                    value={form.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={12} lg={6}>
                            <FormLabel className="fs-16 fw-500">Role</FormLabel>
                            <CustomSelect
                                id="roles"
                                name="roles"
                                backgroundColor="#ffff"
                                value={REPRESENTATIVE_ROLE.find(
                                    (option) => option?.value == form.roles
                                )}
                                options={REPRESENTATIVE_ROLE}
                                border={validation.roles ? "#dc3545" : ""}
                                onChange={(event: any) =>
                                    handleChange('roles', event.value)
                                }
                                isSearchable={false}
                                isMulti={false}
                                disabled={!!editData}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-7 gap-lg-0 gap-md-4">
                        {form.roles === 1 ? (
                            <Col md={12} lg={6}>
                                <FormLabel className="fs-16 fw-500">Select District</FormLabel>
                                <div style={{ border: validation.districts ? "1px solid #dc3545" : "none", borderRadius: "8px" }}>
                                    <CustomComponentSelect
                                        isLoading={fetchLoader}
                                        options={districts}
                                        onChange={(e: any) => {
                                            handleSelectDistrict(e);
                                        }}
                                        onMenuClose={onMenuDistrictClose}
                                        value={districtDetails}
                                        defaultValue={districtDetails}
                                        hideSelectedOptions={false}
                                        showCountInMultiValue={false}
                                        closeMenuOnSelect={false}
                                        isMulti={true}
                                        isDisabled={fetchLoader}
                                    />
                                </div>
                                {assignedDistricts.length > 0 && (
                                    <div className="text-danger fs-14 mt-2">
                                        Districts ({assignedDistricts.map((d: any) => d.name).join(", ")}) are already assigned. By continuing, existing managers will be removed.
                                    </div>
                                )}
                                {removedDistricts.length > 0 && (
                                    <div className="text-warning fs-14 mt-2">
                                        Districts ({removedDistricts.map((d: any) => d.name).join(", ")}) have been removed. By continuing, all related data under the manager will be removed.
                                    </div>
                                )}
                            </Col>
                        ) : <></>}
                        {form.roles === 2 || form.roles === 7 ? (
                            <Col md={12} lg={6}>
                                <FormLabel className="fs-16 fw-500">Select Manager</FormLabel>
                                <CustomSelectWhite
                                    loading={fetchLoader}
                                    options={salesUsers}
                                    // onMenuScrollToBottom={onMenuSalesUsersScrollToBottom}
                                    onMenuClose={() => {
                                        onMenuSalesUsersClose();
                                    }}
                                    value={
                                        salesUsers.find(
                                            (item: any) => item._id == form?.salesManagerId
                                        ) || null
                                    }
                                    onMenuOpen={() => {
                                        onMenuSalesUsersOpen();
                                    }}
                                    onInputChange={(newValue: any, { action }: any) => {
                                        if (action === "input-change") {
                                            handleSalesUsersInputChange(newValue);
                                        }
                                    }}
                                    onChange={(e: any) => {
                                        handleAssignSalesUser(e);
                                    }}
                                    border={validation.salesManagerId ? "#dc3545" : ""}
                                    isMulti={false}
                                />
                            </Col>
                        ) : <></>}
                    </Row>

                    <Button variant="primary" className="uw-btn uw-btn-primary uw-btn-lg w-100 btn-lg shadow-sm" onClick={handleSave} disabled={loading || fetchLoader}>
                        {!loading ? (
                            <span className="fs-16 fw-bold">
                                {assignedDistricts.length > 0 ? "Remove & Save" : "Save"}
                            </span>
                        ) : (
                            <div className="d-flex justify-content-center">
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </div>
                        )}
                    </Button>
                </Form>
            </Modal.Body>
            {/* <DistrictAlreadyAssigned
                show={showAssignedModal}
                onHide={() => setShowAssignedModal(false)}
                districtIds={assignedDistrictIds}
                onSave={handleRemoveAndSave}
            /> */}
        </Modal>
    );
};

export default AddSalesUserModal;
